import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  ExternalLink,
  Loader2,
  Crown,
  Zap,
  Infinity,
  Receipt,
  Calendar,
  Settings,
  RefreshCw,
  AlertCircle
} from '@/components/icons';
import { useAuth } from '../Auth';
import { customer } from '../../lib/auth-client';
import { Tooltip } from '@/components/ui/beui';

interface Subscription {
  id: string;
  status: string;
  currentPeriodEnd?: string;
  productId: string;
}

interface SubscriptionStatus {
  subscriptionTier: 'free' | 'pro' | 'lifetime';
  polarCustomerId: string | null;
  subscriptionId: string | null;
  subscriptionStartedAt: string | null;
  subscriptionExpiresAt: string | null;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export function BillingManagement() {
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshSession } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  // Get current user's subscription tier - prefer synced status over user object
  const currentTier = subscriptionStatus?.subscriptionTier ||
    (user as { subscriptionTier?: string } | null)?.subscriptionTier || 'free';

  // Get subscription expiry date
  const subscriptionExpiresAt = subscriptionStatus?.subscriptionExpiresAt;

  // Find the relevant subscription (prioritize active, then canceling)
  // Check if subscription is canceling by checking the actual subscription status
  // Only show cancellation message if subscription status is 'canceled' or 'canceling'
  const activeSubscription = subscriptions.find(sub => sub.status?.toLowerCase() === 'active') ||
                             subscriptions.find(sub => sub.status?.toLowerCase() === 'canceling') ||
                             subscriptions.find(sub => sub.status?.toLowerCase() === 'canceled') ||
                             (subscriptions.length > 0 ? subscriptions[0] : null);
  const subscriptionStatusValue = activeSubscription?.status?.toLowerCase();
  const isCanceling = currentTier === 'pro' &&
    (subscriptionStatusValue === 'canceled' || subscriptionStatusValue === 'canceling');

  const getTierIcon = () => {
    switch (currentTier.toLowerCase()) {
      case 'pro':
        return <Crown className="w-6 h-6" />;
      case 'lifetime':
        return <Infinity className="w-6 h-6" />;
      default:
        return <Zap className="w-6 h-6" />;
    }
  };

  const getTierGradient = () => {
    switch (currentTier.toLowerCase()) {
      case 'pro':
        return 'from-primary to-primary/70';
      case 'lifetime':
        return 'from-amber-500 to-orange-600';
      default:
        return 'from-muted-foreground/70 to-muted-foreground';
    }
  };

  const getTierName = () => {
    return currentTier.charAt(0).toUpperCase() + currentTier.slice(1);
  };

  // Sync subscription status from Polar
  const syncSubscription = useCallback(async (shouldRefreshSession = false) => {
    if (!isAuthenticated) return;

    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/subscription/sync`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to sync subscription');
      }

      const data = await response.json();
      setSubscriptionStatus(data);

      // Only refresh session when explicitly requested (e.g., manual sync button)
      if (shouldRefreshSession && refreshSession) {
        await refreshSession();
      }
    } catch (err) {
      console.error('Error syncing subscription:', err);
      // Don't show error - just use cached data
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, refreshSession]);

  // Fetch subscriptions and sync on mount - only runs once when authenticated
  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // First sync subscription status from Polar (without session refresh)
      await syncSubscription(false);

      // Then fetch subscriptions list - only if user has a Polar customer ID
      // Check if user has polarCustomerId from subscriptionStatus or user object
      const userWithPolarId = user as { polarCustomerId?: string | null } | null;
      const hasPolarCustomerId = subscriptionStatus?.polarCustomerId || userWithPolarId?.polarCustomerId;

      if (hasPolarCustomerId) {
        try {
          // Fetch subscriptions - don't filter by active only, so we can see canceled/canceling status
          const result = await customer.subscriptions.list({
            query: {
              page: 1,
              limit: 10,
              // Remove active: true to get all subscriptions including canceled ones
            },
          });
          if (isMounted && result.data) {
            setSubscriptions(result.data as unknown as Subscription[]);
          }
        } catch (err) {
          console.error('Error fetching subscriptions:', err);
          // Don't show error for empty subscriptions - user might not have any
          // Set empty array to avoid showing error state
          if (isMounted) {
            setSubscriptions([]);
          }
        }
      } else {
        // User doesn't have a Polar customer ID, so they have no subscriptions
        if (isMounted) {
          setSubscriptions([]);
        }
      }

      if (isMounted) {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
    // syncSubscription/user/polarCustomerId intentionally excluded: including
    // them would re-run this fetch in a loop. Runs only on auth-state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setError(null);

    try {
      await customer.portal();
    } catch (err) {
      console.error('Portal error:', err);
      setError('Failed to open customer portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="px-6 pb-16 pt-6 md:px-8">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4 text-foreground">
            Sign in to manage billing
          </h1>
          <p className="mb-8 text-muted-foreground">
            You need to be signed in to view your subscription and billing information.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="px-6 pb-16 pt-6 md:px-8">
        <div className="max-w-3xl mx-auto px-4 py-16 flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin mb-4 text-primary" />
          <p className="text-muted-foreground">
            Loading subscription info...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pb-16 pt-6 md:px-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">
            Billing &amp; Subscription
          </h1>
          <Tooltip content="Refresh subscription status from Polar">
            <button
              onClick={() => syncSubscription(true)}
              disabled={isSyncing || isLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50"
              aria-label="Refresh subscription status from Polar"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Refresh'}
            </button>
          </Tooltip>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
            {error}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="rounded-xl p-6 mb-6 bg-card border border-border shadow-sm">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getTierGradient()} flex items-center justify-center text-white`}>
                {getTierIcon()}
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">
                  {getTierName()} Plan
                </h2>
                <p className="text-sm text-muted-foreground">
                  {currentTier === 'free'
                    ? 'Limited features'
                    : currentTier === 'lifetime'
                      ? 'Lifetime access - No recurring payments'
                      : 'Full access to all features'}
                </p>
              </div>
            </div>

            {currentTier === 'free' && (
              <button
                onClick={() => navigate('/pricing')}
                className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
              >
                Upgrade
              </button>
            )}
          </div>

          {/* Subscription Details */}
          {currentTier !== 'free' && (
            <div className="border-t border-border pt-4">
              {currentTier === 'lifetime' ? (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Never expires
                  </span>
                </div>
              ) : isCanceling && subscriptionExpiresAt ? (
                <div className="flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-500">
                    Cancels on {new Date(subscriptionExpiresAt).toLocaleDateString()} — You&apos;ll retain Pro access until then
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {subscriptionExpiresAt || activeSubscription?.currentPeriodEnd
                      ? `Renews on ${new Date(subscriptionExpiresAt || activeSubscription!.currentPeriodEnd!).toLocaleDateString()}`
                      : 'Active subscription'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Customer Portal */}
          <button
            onClick={handleOpenPortal}
            disabled={portalLoading || currentTier === 'free'}
            className={`flex items-center gap-4 p-5 rounded-xl text-left transition-all bg-card border border-border shadow-sm hover:border-muted-foreground/30 ${
              currentTier === 'free' ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted">
              {portalLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              ) : (
                <Settings className="w-5 h-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">
                Manage Subscription
              </h3>
              <p className="text-sm text-muted-foreground">
                {currentTier === 'free'
                  ? 'Subscribe to manage billing'
                  : 'View invoices, update payment method'}
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* View Pricing */}
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-4 p-5 rounded-xl text-left transition-all bg-card border border-border shadow-sm hover:border-muted-foreground/30"
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">
                View Plans
              </h3>
              <p className="text-sm text-muted-foreground">
                Compare features and pricing
              </p>
            </div>
            <ArrowLeft className="w-4 h-4 rotate-180 text-muted-foreground" />
          </button>
        </div>

        {/* Recent Orders - placeholder for future */}
        {currentTier !== 'free' && (
          <div className="rounded-xl p-6 bg-card border border-border shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">
                Payment History
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              View your complete payment history in the customer portal.
            </p>
            <button
              onClick={handleOpenPortal}
              disabled={portalLoading}
              className="mt-4 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Open Customer Portal →
            </button>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Need help? Contact us at{' '}
            <a
              href="mailto:support@stacksketch.site"
              className="font-medium text-primary"
            >
              support@stacksketch.site
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default BillingManagement;
